<template>
  <v-container v-if="!isLoading" style="max-width: 1200px">
    <div class="d-flex">
      <v-btn
        icon="mdi-close"
        variant="text"
        density="comfortable"
        class="ms-n14 me-5 bg-accent"
        @click="goBack"
      />
      <h2 class="mb-4">
        Buchungsobjekt {{ this.mode === "create" ? "erstellen" : "bearbeiten" }}
      </h2>
    </div>
    <v-row>
      <v-col>
        <v-select
          bg-color="accent"
          variant="filled"
          label="Typ"
          hide-details
          v-model="type"
          :items="bookableTypes"
          item-title="title"
          item-value="key"
          disabled
        ></v-select>
      </v-col>
      <v-col>
        <v-text-field
          bg-color="accent"
          variant="filled"
          label="Mandant"
          hide-details
          disabled
          v-model="tenantId"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row v-if="type === 'ticket'">
      <v-col>
        <v-select
          bg-color="accent"
          variant="filled"
          label="Veranstaltung"
          hide-details
          v-model="eventId"
          item-value="id"
          name="information.name"
          item-title="information.name"
          :items="events"
        ></v-select>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-text-field
          bg-color="accent"
          variant="filled"
          label="Bezeichnung"
          hide-details
          v-model="title"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <ChooseFile
          v-model="imgUrl"
          :allow-protected="false"
          :tenant-id="tenantId"
          filled
          images-only
          label="Cover-Bild"
          background-color="accent"
          forced-subdirectory="rooms"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <Tiptap v-model="description" label="Beschreibung"></Tiptap>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-text-field
          bg-color="accent"
          variant="filled"
          label="Ort"
          hide-details
          v-model="location"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-combobox
          v-if="this.isInstanceOwner || this.isTenantOwner"
          v-model="tags"
          :items="tagsAvailable"
          label="Tags"
          hide-selected
          no-data-text="Keine Tags angelegt"
          multiple
          bg-color="accent"
          clearable
          chips
          variant="filled"
          :maxlength="50"
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              variant="flat"
              color="secondary"
              @click="select"
              @click:close="removeTags(item?.title)"
            >
              <span>{{ item?.title }}</span>
            </v-chip>
          </template>
        </v-combobox>
        <v-select
          v-else
          v-model="tags"
          :items="tagsAvailable"
          label="Tags"
          hide-selected
          no-data-text="Keine Tags angelegt"
          multiple
          bg-color="accent"
          clearable
          chips
          variant="filled"
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              variant="flat"
              color="secondary"
              @click="select"
              @click:close="removeTags(item?.title)"
            >
              <span>{{ item?.title }}</span>
            </v-chip>
          </template>
        </v-select>
      </v-col>
      <v-col>
        <v-combobox
          v-if="this.isInstanceOwner || this.isTenantOwner"
          v-model="flags"
          :items="flagsAvailable"
          label="Flags"
          hide-selected
          no-data-text="Keine Flags angelegt"
          multiple
          bg-color="accent"
          clearable
          chips
          variant="filled"
          :maxlength="50"
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              variant="flat"
              color="secondary"
              @click="select"
              @click:close="removeFlags(item?.title)"
            >
              <span>{{ item?.title }}</span>
            </v-chip>
          </template>
        </v-combobox>
        <v-select
          v-else
          v-model="flags"
          :items="flagsAvailable"
          label="Flags"
          hide-selected
          no-data-text="Keine Flags angelegt"
          multiple
          bg-color="accent"
          clearable
          chips
          variant="filled"
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              color="secondary"
              @click="select"
              @click:close="removeFlags(item?.title)"
            >
              <span>{{ item?.title }}</span>
            </v-chip>
          </template>
        </v-select>
      </v-col>
    </v-row>
    <LocationPicker
      v-if="type !== 'ticket'"
      objectType="bookable"
      :latitude="latitude"
      :longitude="longitude"
      @update:latitude="latitude = $event"
      @update:longitude="longitude = $event"
    />

    <h3 class="mt-10 mb-4">Preis</h3>
    <v-row>
      <v-col cols="12" md="3">
        <v-select
          bg-color="accent"
          variant="filled"
          label="Preisart"
          hide-details
          v-model="priceType"
          :items="priceTypes"
          item-title="name"
          item-value="id"
        ></v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          bg-color="accent"
          type="number"
          hide-spin-buttons
          variant="filled"
          label="Verfügbare Anzahl"
          hide-details
          v-model="amount"
          :suffix="priceType === 'per-square-meter' ? 'm²' : 'Stück'"
        ></v-text-field>
      </v-col>
      <v-col v-if="!useGraduatedPrices" cols="12" md="2">
        <v-text-field
          v-if="priceCategories[0]"
          type="number"
          hide-spin-buttons
          bg-color="accent"
          variant="filled"
          label="Preis (netto)"
          hide-details
          v-model="priceCategories[0].priceEur"
          suffix="Euro"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          bg-color="accent"
          type="number"
          hide-spin-buttons
          variant="filled"
          label="MwSt."
          hide-details
          v-model="priceValueAddedTax"
          suffix="%"
        ></v-text-field>
      </v-col>

      <v-col v-if="!useGraduatedPrices" cols="12" md="2">
        <v-tooltip location="top" max-width="300" open-delay="400">
          <template v-slot:activator="{ props }">
            <div v-bind="props">
              <v-checkbox
                v-if="priceCategories[0]"
                v-model="priceCategories[0].fixedPrice"
                label="Pauschalpreis"
              >
              </v-checkbox>
            </div>
          </template>
          <span> Bei Aktivierung wird immer der Grundpreis berechnet. </span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="!useGraduatedPrices">
        <v-btn
          dense
          variant="outlined"
          hide-details
          @click="useGraduatedPrices = true"
        >
          Staffelpreise
        </v-btn>
      </v-col>
    </v-row>

    <v-card v-if="useGraduatedPrices" flat border rounded class="mt-3">
      <v-card-subtitle
        class="d-flex justify-space-between align-center mb-4"
        style="
          background-color: rgb(var(--v-theme-accent));
          height: 60px;
          opacity: 1;
        "
      >
        <span class="text-h6">Preis-Kategorien</span>
      </v-card-subtitle>
      <v-card-text>
        <div v-for="(price, idx) in priceCategories" :key="idx">
          <div class="d-flex">
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  bg-color="accent"
                  type="number"
                  hide-spin-buttons
                  variant="filled"
                  label="Preis (netto)"
                  hide-details
                  v-model="price.priceEur"
                  suffix="Euro"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="price.interval.start"
                  bg-color="accent"
                  variant="filled"
                  label="Gültig ab"
                  type="number"
                  :suffix="intervalSuffix"
                  @blur="checkNull('price.interval.start')"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model="price.interval.end"
                  bg-color="accent"
                  variant="filled"
                  label="Gültig bis"
                  type="number"
                  :suffix="intervalSuffix"
                  @blur="checkNull('price.interval.start')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-tooltip location="top" max-width="300" open-delay="400">
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-checkbox
                        v-model="price.fixedPrice"
                        label="Pauschalpreis"
                      >
                      </v-checkbox>
                    </div>
                  </template>
                  <span>
                    Bei Aktivierung wird immer der Grundpreis berechnet.
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
            <v-btn
              icon
              variant="text"
              @click="removePriceCategory(idx)"
              class="mt-4"
              color="error"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
          <v-divider
            v-if="
              priceCategories.length > 1 && idx !== priceCategories.length - 1
            "
            class="mb-5"
          ></v-divider>
        </div>
        <div>
          <v-btn variant="outlined" class="mt-2" @click="addPriceCategory"
            >Neue Preis-Kategorie</v-btn
          >
        </div>
      </v-card-text>
    </v-card>

    <h3 class="mt-10 mb-4">Öffnungszeiten und Buchungszeiträume</h3>
    <BookableTimeDependantAttributes
      :bookable-type="type"
    ></BookableTimeDependantAttributes>

    <v-row>
      <v-col cols="12" md="3">
        <v-tooltip :disabled="isRecurringAllowedContext" location="top">
          <template v-slot:activator="{ props }">
            <div v-bind="props">
              <v-switch
                :disabled="!isRecurringAllowedContext"
                density="compact"
                label="Wiederkehrende Buchungen zulassen"
                hide-details
                v-model="allowRecurringBooking"
                @update:model-value="
                  !isScheduleRelated &&
                  !isTimePeriodRelated &&
                  !isLongRangeRelated
                    ? (allowRecurringBooking = false)
                    : null
                "
              ></v-switch>
            </div>
          </template>
          <span>
            Wiederkehrende Buchungen sind nur für Buchungen über den Kalender,
            feste Zeiträume, Kalenderwochen und ganze Monate möglich.</span
          >
        </v-tooltip>
      </v-col>
      <v-col cols="12" md="3">
        <v-switch
          density="compact"
          label="Buchungen automatisch freigeben"
          hide-details
          v-model="autoCommitBooking"
        ></v-switch>
      </v-col>
      <v-col md="3" sm="12">
        <v-switch
          density="compact"
          label="Buchungsobjekt ist buchbar"
          hide-details
          v-model="isBookable"
        ></v-switch>
      </v-col>
      <v-col md="3" sm="12">
        <v-tooltip :disabled="allowPublic" location="top">
          <template v-slot:activator="{ props }">
            <div v-bind="props">
              <v-switch
                :disabled="!allowPublic"
                density="compact"
                label="Buchungsobjekt ist sichtbar"
                hide-details
                v-model="isPublic"
              ></v-switch>
            </div>
          </template>
          <span>Maximale Anzahl öffentlicher Buchungsobjekte erreicht</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <h3 class="mt-10">Schließsysteme</h3>
    <v-row>
      <v-col>
        <p>
          Buchungsobjekte, die mit Schließsystemen verbunden sind, können
          automatisch geöffnet und geschlossen werden.
        </p>
      </v-col>
    </v-row>
    <BookableLockingAttributes
      v-if="amount"
      :tenant-id="tenantId"
      :amount="amount"
    ></BookableLockingAttributes>

    <v-alert
      class="mt-4"
      v-else
      type="warning"
      density="compact"
      variant="outlined"
    >
      Um Schließsysteme zu konfigurieren, geben Sie bitte die Anzahl der
      verfügbaren Buchungsobjekte an.
    </v-alert>

    <h3 class="mt-10">Individuelle Berechtigungen</h3>

    <v-row>
      <v-col>
        <p>
          Berechtigen Sie <strong>bestimmte Benutzer</strong>, dieses Objekt zu
          sehen. Werden keine Benutzer explizit zur Ansicht berechtigt, bleibt
          dieses Buchungsobjekt für öffentlich einsehbar.
        </p>

        <v-autocomplete
          class="mt-3"
          v-model="permittedUsers"
          :items="availableUsers"
          label="Verfügbar für Benutzer"
          hide-selected
          no-data-text="Keine Benutzer verfügbar"
          multiple
          bg-color="accent"
          clearable
          chips
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              color="secondary"
              variant="flat"
              @click="select"
              @click:close="removePermittedUser(item?.value)"
            >
              <span>{{ item?.value }}</span>
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p>
          Berechtigen Sie <strong>alle Benutzer einer Benutzergruppe</strong>,
          dieses Objekt zu sehen. Werden keine Benutzer explizit zur Ansicht
          berechtigt, bleibt dieses Buchungsobjekt öffentlich einsehbar.
        </p>

        <v-autocomplete
          class="mt-3"
          v-model="permittedUserGroups"
          :items="availableUserGroups"
          label="Verfügbar für Benutzergruppen"
          item-title="name"
          item-value="id"
          hide-selected
          no-data-text="Keine Benutzergruppen verfügbar"
          multiple
          bg-color="accent"
          clearable
          chips
          auto-grow
          :return-object="false"
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              variant="flat"
              color="secondary"
              @click="select"
              @click:close="removePermittedUserGroup(item.value)"
            >
              <span>{{
                availableUserGroups.find((r) => r.id === item.value)?.name
              }}</span>
            </v-chip>
          </template>

          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props">
              <template v-slot:title>
                <div class="d-flex align-center" style="gap: 0.5rem">
                  <span>{{ item.raw ? item.raw.name : item.name }}</span>
                  <span class="text-caption" style="color: rgba(0, 0, 0, 0.6)">
                    {{
                      (item.raw ? item.raw.tenantId : item.tenantId) === null
                        ? "(Instanz-Ebene)"
                        : "(Mandanten-Ebene)"
                    }}
                  </span>
                </div>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <h3 class="mt-10">Kostenfreie Buchungen</h3>
    <v-row>
      <v-col>
        <p>Berechtigen Sie Nutzer dieses Objekt kostenfrei zu buchen.</p>

        <v-autocomplete
          class="mt-3"
          v-model="freeBookingUsers"
          :items="availableUsers"
          label="Kostenfrei für Benutzer"
          hide-selected
          no-data-text="Keine Benutzer verfügbar"
          multiple
          bg-color="accent"
          clearable
          chips
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              color="secondary"
              variant="flat"
              @click="select"
              @click:close="removeFreeBookingUser(item?.value)"
            >
              <span>{{ item?.value }}</span>
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p>
          Berechtigen Sie <strong>alle Benutzer einer Benutzergruppe</strong>,
          dieses Objekt kostenfrei zu buchen.
        </p>

        <v-autocomplete
          class="mt-3"
          v-model="freeBookingUserGroups"
          :items="availableUserGroups"
          label="Benutzergruppen, die kostenlos buchen können"
          item-title="name"
          item-value="id"
          hide-selected
          no-data-text="Keine Benutzergruppen verfügbar"
          multiple
          bg-color="accent"
          clearable
          chips
          :return-object="false"
        >
          <template v-slot:chip="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              closable
              color="secondary"
              variant="flat"
              @click="select"
              @click:close="removeFreeBookingUserGroup(item.value)"
            >
              <span>{{
                availableUserGroups.find((r) => r.id === item.value)?.name
              }}</span>
            </v-chip>
          </template>

          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props">
              <template v-slot:title>
                <div class="d-flex align-center" style="gap: 0.5rem">
                  <span>{{ item.raw ? item.raw.name : item.name }}</span>
                  <span class="text-caption" style="color: rgba(0, 0, 0, 0.6)">
                    {{
                      (item.raw ? item.raw.tenantId : item.tenantId) === null
                        ? "(Instanz-Ebene)"
                        : "(Mandanten-Ebene)"
                    }}
                  </span>
                </div>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <h2 class="mt-10">Beziehungen zu anderen Buchungsobjekten</h2>

    <h3 class="mt-5">Zusätzliche Buchungsoptionen</h3>
    <p>
      Buchungsobjekte, die Sie als zusätzliche Buchungsoptionen definieren,
      werden ihren Kund*innen beim Checkout als ergänzende Buchungsobjekte
      angezeigt.
    </p>
    <v-row>
      <v-col>
        <BookableCheckoutBookables
          :items="checkoutBookableIds"
          :available-items="bookablesWithoutSelf"
        >
        </BookableCheckoutBookables>
      </v-col>
    </v-row>

    <h3 class="mt-10">Abhängige Objekte (Hierarchie)</h3>
    <p>
      Eine Buchung ist nur möglich, wenn das übergeordnete Objekt verfügbar ist
      und keines seiner untergeordneten Objekte bereits zur gleichen Zeit
      gebucht ist.
    </p>
    <p>Fügen Sie hier die untergeordneten Objekte hinzu.</p>
    <v-row>
      <v-col>
        <SortableList
          :items="relatedBookableIds"
          :available-items="bookablesWithoutSelf"
          item-value="id"
          item-text="title"
          item-detail="type"
        >
          <template v-slot:detail="{ itemObject }">
            {{ itemLabel(`editBookables.types.${itemObject.type}`) }}
          </template>
        </SortableList>
      </v-col>
    </v-row>

    <h3 class="mt-10 mb-4">Anhänge</h3>
    <div v-for="(attachment, index) in attachments" :key="attachment.id">
      <v-card flat border rounded>
        <v-card-text>
          <v-row>
            <v-col>
              <v-row>
                <v-col>
                  <v-text-field
                    bg-color="accent"
                    variant="filled"
                    label="Titel"
                    hide-details
                    v-model="attachment.title"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-select
                    bg-color="accent"
                    variant="filled"
                    label="Typ"
                    hide-details
                    v-model="attachment.type"
                    :items="attachmentTypes"
                    item-title="name"
                    item-value="id"
                  ></v-select>
                </v-col>
                <v-col>
                  <ChooseFile
                    v-model="attachment.url"
                    :allow-protected="false"
                    :tenant-id="tenantId"
                    filled
                    label="Datei"
                    background-color="accent"
                    forced-subdirectory="agreements"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    bg-color="accent"
                    variant="filled"
                    label="Beschreibung"
                    placeholder="Ich habe die Nutzungsbedingungen gelesen und akzeptiere sie."
                    hide-details
                    v-model="attachment.caption"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-switch
                    label="Im Buchungsprozess anzeigen"
                    hide-details
                    v-model="attachment.show"
                  ></v-switch>
                </v-col>
                <v-col>
                  <v-switch
                    label="Muss vom Nutzer akzeptiert werden"
                    hide-details
                    v-model="attachment.required"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="auto">
              <v-btn
                icon
                density="compact"
                variant="text"
                size="small"
                @click="removeAttachment(attachment.id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-divider
        class="my-5"
        v-if="index < attachments.length - 1"
        :key="`divider-${index}`"
      />
    </div>
    <v-row>
      <v-col cols="auto">
        <v-btn variant="outlined" class="mt-2" @click="addNewAttachment()"
          >Neuer Anhang</v-btn
        >
      </v-col>
    </v-row>
    <h3 class="mt-10 mb-4">Zusätzliche Optionen</h3>
    <v-row>
      <v-col cols="auto">
        <v-switch
          density="compact"
          label="Firma erforderlich"
          hide-details
          v-model="companyRequired"
        ></v-switch>
      </v-col>
      <v-col cols="auto">
        <v-switch
          density="compact"
          label="Kommentarfeld erforderlich"
          hide-details
          v-model="commentRequired"
        ></v-switch>
      </v-col>
    </v-row>

    <h3 class="mt-10 mb-4">Buchungshinweise</h3>
    <v-row>
      <v-col>
        <Tiptap v-model="bookingNotes" label="Buchungshinweise"></Tiptap>
      </v-col>
    </v-row>

    <v-divider class="mt-10"></v-divider>

    <div class="d-flex mt-2">
      <v-spacer></v-spacer>
      <v-btn
        size="large"
        color="primary"
        elevation="0"
        class="me-3"
        @click="createOrUpdate"
        >{{ this.mode === "create" ? "Erstellen" : "Speichern" }}</v-btn
      >
      <v-btn size="large" variant="outlined" elevation="0" @click="goBack"
        >Abbrechen</v-btn
      >
    </div>

    <!--{{bookable}}-->
  </v-container>
</template>

<script>
import ApiBookablesService from "@/services/api/ApiBookablesService";
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import ApiEventService from "@/services/api/ApiEventService";
import ApiUsersService from "@/services/api/ApiUsersService";
import ApiTenantService from "@/services/api/ApiTenantService";
import BookableTimeDependantAttributes from "@/components/Bookable/BookableTimeDependantAttributes";
import SortableList from "@/components/SortableList";
import Tiptap from "@/components/Tiptap";
import ApiUserGroupService from "../../services/api/ApiUserGroupService";
import ChooseFile from "@/components/Files/ChooseFile.vue";
import BookableLockingAttributes from "@/components/Bookable/BookableLockingAttributes";
import BookableCheckoutBookables from "@/components/Bookable/BookableCheckoutBookables.vue";
import LocationPicker from "@/components/LocationPicker.vue";
import ApiTagsService from "../../services/api/ApiTagsService";
import ApiFlagsService from "../../services/api/ApiFlagsService";
import Matomo from "@/utils/Matomo";

export default {
  name: "EditBookable",
  components: {
    BookableCheckoutBookables,
    ChooseFile,
    SortableList,
    BookableTimeDependantAttributes,
    Tiptap,
    BookableLockingAttributes,
    LocationPicker,
  },

  data() {
    return {
      useGraduatedPrices: false,
      allowPublic: true,
      bookableType: null,
      bookable: {},
      bookableTypes: [
        {
          title: "Veranstaltungsort",
          key: "event-location",
        },
        {
          title: "Raum",
          key: "room",
        },
        {
          title: "Resource",
          key: "resource",
        },
        {
          title: "Ticket",
          key: "ticket",
        },
      ],
      attachmentTypes: [
        {
          id: "agreement",
          name: "Nutzervereinbarung",
        },
        {
          id: "privacy-agreement",
          name: "Datenschutzerklärung",
        },
        {
          id: "user-manual",
          name: "Betriebsanleitung",
        },
        {
          id: "security-information",
          name: "Sicherheitshinweise",
        },
        {
          id: "product-information",
          name: "Produktinformationen",
        },
      ],
      priceTypes: [
        {
          id: "per-item",
          name: "pro Stück",
        },
        {
          id: "per-hour",
          name: "pro Stunde",
        },
        {
          id: "per-day",
          name: "pro Tag",
        },
        {
          id: "per-square-meter",
          name: "pro m²",
        },
      ],
      tagsAvailable: [],
      flagsAvailable: [],
      events: [],
      bookables: [],
      weekdays: [
        {
          id: 1,
          name: "Montag",
        },
        {
          id: 2,
          name: "Dienstag",
        },
        {
          id: 3,
          name: "Mittwoch",
        },
        {
          id: 4,
          name: "Donnerstag",
        },
        {
          id: 5,
          name: "Freitag",
        },
        {
          id: 6,
          name: "Samstag",
        },
        {
          id: 0,
          name: "Sonntag",
        },
      ],
      availableUsers: [],
      availableUserGroups: [],
    };
  },
  watch: {
    attachments: {
      handler: function (val) {
        val.forEach((attachment) => {
          if (attachment.required === true) {
            attachment.show = true;
          }
        });
      },
      deep: true,
    },
    priceCategories: {
      handler: function () {
        if (!this.useGraduatedPrices) {
          this.setUseGraduatedPrices();
        }
      },
      deep: true,
    },
    isRecurringAllowedContext(newVal) {
      if (!newVal) {
        this.allowRecurringBooking = false;
      }
    },
  },
  methods: {
    ...mapActions({
      updateValue: "bookables/updateForm",
      removeAttachment: "bookables/removeAttachment",
      addAttachment: "bookables/addAttachment",
      clearForm: "bookables/clearForm",
      restoreFromApi: "bookables/restoreFromApi",
      removeTimePeriod: "bookables/removeTimePeriod",
      startLoading: "loading/start",
      stopLoading: "loading/stop",
    }),
    checkNull(field) {
      if (this[field] === "") {
        this[field] = null;
      }
    },
    setUseGraduatedPrices() {
      this.useGraduatedPrices = !!(
        this.priceCategories.length > 1 ||
        this.priceCategories.some((pC) => pC.interval.start !== null) ||
        this.priceCategories.some((pC) => pC.interval.end !== null)
      );
    },
    addPriceCategory() {
      const lastCategory =
        this.priceCategories[this.priceCategories.length - 1];
      this.priceCategories.push({
        priceEur: 0,
        priceValueAddedTax: 0,
        interval: {
          start: lastCategory ? lastCategory.interval.end : null,
          end: null,
        },
        fixedPrice: false,
      });
    },
    removePriceCategory(index) {
      // reset interval and disable graduated pricing if only one category remains
      if (this.priceCategories.length === 1) {
        this.priceCategories[0].interval.start = null;
        this.priceCategories[0].interval.end = null;
        this.useGraduatedPrices = false;
      } else {
        // remove the selected price category
        this.priceCategories.splice(index, 1);
      }
    },
    itemLabel(key) {
      return this.$i18n.t(key);
    },
    addNewAttachment() {
      this.addAttachment({
        id: uuidv4(),
        title: "",
        caption: "",
        type: "",
        url: "",
      });
    },
    prepareCreateForm() {
      // Clear all form fields
      this.clearForm();

      // Set default bookable type based on route meta settings
      this.updateValue({
        field: "type",
        value: this.$router.currentRoute.value.meta.type,
      });
    },
    async createOrUpdate() {
      this.startLoading("crud-bookable");
      ApiBookablesService.createOrUpdateBookable()
        .then(() => {
          if (this.mode === "create") {
            Matomo.trackEvent([
              "trackEvent",
              "Bookable",
              "Create",
              this.title || "(no title)",
            ]);
          }
        })
        .then(() => {
          this.goBack();
        })
        .finally(() => {
          this.stopLoading("crud-bookable");
        });
    },
    fetchBookable(bookableId) {
      this.startLoading("fetch-bookable");
      ApiBookablesService.getBookable(bookableId)
        .then((response) => {
          const {
            attachments,
            parent,
            amount,
            allowRecurringBooking,
            autoCommitBooking,
            minBookingDuration,
            maxBookingDuration,
            description,
            isScheduleRelated,
            isTimePeriodRelated,
            isOpeningHoursRelated,
            isSpecialOpeningHoursRelated,
            timePeriods,
            openingHours,
            specialOpeningHours,
            flags,
            id,
            location,
            coordinates,
            priceCategories,
            priceType,
            priceValueAddedTax,
            tags,
            tenantId,
            title,
            type,
            eventId,
            relatedBookableIds,
            checkoutBookableIds,
            imgUrl,
            isBookable,
            isPublic,
            permittedUsers,
            permittedUserGroups,
            freeBookingUsers,
            freeBookingUserGroups,
            isLongRange,
            longRangeOptions,
            lockerDetails,
            requiredFields,
            bookingNotes,
          } = response.data;

          this.restoreFromApi({
            id: id,
            parent: parent,
            tenantId: tenantId,
            type: type,
            title: title,
            description: description,
            location: location,
            coordinates: coordinates,
            priceCategories: priceCategories || [
              {
                priceEur: 0,
                interval: {
                  start: null,
                  end: null,
                },
                fixedPrice: false,
              },
            ],
            priceType: !_.isNil(priceType) ? priceType : false,
            priceValueAddedTax: !_.isNil(priceValueAddedTax)
              ? priceValueAddedTax
              : 0,
            amount: !_.isNil(amount) ? amount : null,
            isScheduleRelated: !_.isNil(isScheduleRelated)
              ? isScheduleRelated
              : false,
            isTimePeriodRelated: !_.isNil(isTimePeriodRelated)
              ? isTimePeriodRelated
              : false,
            isOpeningHoursRelated: !_.isNil(isOpeningHoursRelated)
              ? isOpeningHoursRelated
              : false,
            isSpecialOpeningHoursRelated: !_.isNil(isSpecialOpeningHoursRelated)
              ? isSpecialOpeningHoursRelated
              : false,
            minBookingDuration: !_.isNil(minBookingDuration)
              ? minBookingDuration
              : null,
            maxBookingDuration: !_.isNil(maxBookingDuration)
              ? maxBookingDuration
              : null,
            allowRecurringBooking: allowRecurringBooking,
            autoCommitBooking: autoCommitBooking,
            attachments: attachments,
            timePeriods: timePeriods,
            openingHours: openingHours,
            specialOpeningHours: specialOpeningHours ?? [],
            tags: tags,
            flags: flags,
            eventId: eventId,
            relatedBookableIds: relatedBookableIds,
            checkoutBookableIds: checkoutBookableIds || [],
            imgUrl: imgUrl,
            isBookable: isBookable,
            isPublic: isPublic,
            permittedUsers: permittedUsers,
            permittedUserGroups: permittedUserGroups,
            freeBookingUsers: freeBookingUsers,
            freeBookingUserGroups: freeBookingUserGroups,
            isLongRange: isLongRange,
            longRangeOptions: longRangeOptions,
            lockerDetails: lockerDetails,
            requiredFields: requiredFields,
            bookingNotes: bookingNotes,
          });
        })
        .catch((err) => console.log(err))
        .finally(() => {
          this.stopLoading("fetch-bookable");
        });
    },

    async fetchEvents() {
      await ApiEventService.getEvents().then((result) => {
        this.events = result?.data;
      });
    },

    async fetchBookables() {
      await ApiBookablesService.getBookables().then((result) => {
        this.bookables = result?.data;
      });
    },

    async fetchTenantUsers(currentTenantId) {
      await ApiTenantService.getTenantUsers(currentTenantId)
        .then((result) => {
          const owners = result?.owner ?? [];
          const userIds = result?.users?.map((user) => user.userId) ?? [];
          this.availableUsers = [...new Set([...owners, ...userIds])];
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async fetchUserGroups() {
      await ApiUserGroupService.getUserGroups().then((result) => {
        this.availableUserGroups = result;
      });
    },

    async fetchTags() {
      this.startLoading("fetch-tags");
      ApiTagsService.getTags()
        .then((response) => {
          this.tagsAvailable = response.data;
        })
        .finally(() => {
          this.stopLoading("fetch-tags");
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async fetchFlags() {
      this.startLoading("fetch-flags");
      ApiFlagsService.getFlags()
        .then((response) => {
          this.flagsAvailable = response.data;
        })
        .finally(() => {
          this.stopLoading("fetch-flags");
        })
        .catch((error) => {
          console.log(error);
        });
    },

    initialize() {
      const bookableId = this.$route.query.id;
      if (!_.isNil(bookableId)) {
        this.fetchBookable(bookableId);
        this.fetchBookables();
      } else {
        this.fetchBookables();
        this.prepareCreateForm();
      }
      this.fetchEvents();
      this.fetchTenantUsers(this.currentTenantId);
      this.fetchUserGroups();
      this.fetchTags();
      this.fetchFlags();
    },

    goBack() {
      if (_.isNil(this.$route.query.fromRoute)) {
        this.$router.push({ name: "dashboard" });
      } else {
        this.$router.push({ name: this.$route.query.fromRoute });
      }
    },
    removeFlags(item) {
      this.flags.splice(this.flags.indexOf(item), 1);
    },
    removeTags(item) {
      this.tags.splice(this.tags.indexOf(item), 1);
    },
    removeRelatedBookableIds(item) {
      this.relatedBookableIds.splice(this.relatedBookableIds.indexOf(item), 1);
    },
    addNewTimePeriod() {
      this.timeStartMenu.push(false);
      this.timeEndMenu.push(false);
      this.timePeriods.push({
        weekdays: null,
        startTime: null,
        endTime: null,
      });
    },
    setEndTime(index, time) {
      this.timePeriods[index].endTime = time;
      this.timeEndMenu[index] = false;
    },
    setStartTime(index, time) {
      this.timePeriods[index].startTime = time;
      this.timeStartMenu[index] = false;
    },
    removeWeekdays(index, item) {
      this.timePeriods[index].weekdays.splice(
        this.timePeriods[index].weekdays.indexOf(item),
        1
      );
    },
    removeTimePeriod(index) {
      this.timePeriods.splice(index, 1);
      this.timeStartMenu.splice(index, 1);
      this.timeEndMenu.splice(index, 1);
    },
    addNewOpeningHours() {
      this.timeStartOpeningHoursMenu.push(false);
      this.timeEndOpeningHoursMenu.push(false);
      this.openingHours.push({
        weekdays: null,
        startTime: null,
        endTime: null,
      });
    },
    setEndOpeningHoursTime(index, time) {
      this.openingHours[index].endTime = time;
      this.timeEndOpeningHoursMenu[index] = false;
    },
    setStartOpeningHoursTime(index, time) {
      this.openingHours[index].startTime = time;
      this.timeStartOpeningHoursMenu[index] = false;
    },
    removeOpeningHoursWeekdays(index, item) {
      this.openingHours[index].weekdays.splice(
        this.openingHours[index].weekdays.indexOf(item),
        1
      );
    },
    removeOpeningHours(index) {
      this.openingHours.splice(index, 1);
      this.timeStartOpeningHoursMenu.splice(index, 1);
      this.timeEndOpeningHoursMenu.splice(index, 1);
    },
    removePermittedUser(item) {
      this.permittedUsers.splice(this.permittedUsers.indexOf(item), 1);
    },
    removePermittedUserGroup(item) {
      this.permittedUserGroups.splice(
        this.permittedUserGroups.indexOf(item),
        1
      );
    },
    removeFreeBookingUser(item) {
      this.freeBookingUsers.splice(this.freeBookingUsers.indexOf(item), 1);
    },
    removeFreeBookingUserGroup(item) {
      this.freeBookingUserGroups.splice(
        this.freeBookingUserGroups.indexOf(item),
        1
      );
    },
    async allowSetPublic() {
      const bookableCountCheck =
        await ApiBookablesService.publicBookableCountCheck();
      this.allowPublic = bookableCountCheck || this.isPublic;
    },
  },
  computed: {
    ...mapGetters({
      isLoading: "loading/isLoading",
      attachments: "bookables/attachments",
      bookableForm: "bookables/form",
      currentTenantId: "tenants/currentTenantId",
      isInstanceOwner: "user/isInstanceOwner",
      isTenantOwner: "user/isTenantOwner",
    }),
    intervalSuffix: {
      get() {
        if (this.priceType === "per-hour") {
          return "Std.";
        } else if (this.priceType === "per-day") {
          return "Tage";
        } else if (this.priceType === "per-square-meter") {
          return "m²";
        } else {
          return "Stück";
        }
      },
    },

    id: {
      get() {
        return this.$store.state.bookables.form.id;
      },
      set(value) {
        this.updateValue({ field: "id", value: value });
      },
    },
    tenantId: {
      get() {
        if (this.mode === "create") {
          return this.currentTenantId;
        } else {
          return this.$store.state.bookables.form.tenantId;
        }
      },
      set(value) {
        this.updateValue({ field: "tenantId", value: value });
      },
    },
    type: {
      get() {
        return this.$store.state.bookables.form.type;
      },
      set(value) {
        this.updateValue({ field: "type", value: value });
      },
    },
    parent: {
      get() {
        return this.$store.state.bookables.form.parent;
      },
      set(value) {
        this.updateValue({ field: "parent", value: value });
      },
    },
    title: {
      get() {
        return this.$store.state.bookables.form.title;
      },
      set(value) {
        this.updateValue({ field: "title", value: value });
      },
    },
    test() {
      return this.$store.state.bookables.form;
    },
    description: {
      get() {
        return this.$store.state.bookables.form.description;
      },
      set(value) {
        this.updateValue({ field: "description", value: value });
      },
    },
    location: {
      get() {
        return this.$store.state.bookables.form.location;
      },
      set(value) {
        this.updateValue({ field: "location", value: value });
      },
    },
    latitude: {
      get() {
        return this.$store.state.bookables.form.coordinates.latitude;
      },
      set(value) {
        const val = value === "" ? null : parseFloat(value);
        this.updateValue({
          parent: "coordinates",
          field: "latitude",
          value: val,
        });
      },
    },
    longitude: {
      get() {
        return this.$store.state.bookables.form.coordinates.longitude;
      },
      set(value) {
        const val = value === "" ? null : parseFloat(value);
        this.updateValue({
          parent: "coordinates",
          field: "longitude",
          value: val,
        });
      },
    },
    priceCategories: {
      get() {
        return this.$store.state.bookables.form.priceCategories;
      },
      set(value) {
        this.updateValue({ field: "priceCategories", value: value });
      },
    },
    priceType: {
      get() {
        return this.$store.state.bookables.form.priceType;
      },
      set(value) {
        this.updateValue({ field: "priceType", value: value });
      },
    },
    priceValueAddedTax: {
      get() {
        return this.$store.state.bookables.form.priceValueAddedTax;
      },
      set(value) {
        this.updateValue({ field: "priceValueAddedTax", value: value });
      },
    },
    amount: {
      get() {
        return this.$store.state.bookables.form.amount;
      },
      set(value) {
        this.updateValue({ field: "amount", value: value });
      },
    },
    isRecurringAllowedContext() {
      return (
        this.isScheduleRelated ||
        this.isTimePeriodRelated ||
        this.isLongRangeRelated
      );
    },
    isLongRangeRelated: {
      get() {
        return this.$store.state.bookables.form.isLongRange;
      },
      set(value) {
        this.updateValue({ field: "isLongRange", value: value });
      },
    },
    isScheduleRelated: {
      get() {
        return this.$store.state.bookables.form.isScheduleRelated;
      },
      set(value) {
        this.updateValue({ field: "isScheduleRelated", value: value });
      },
    },
    isTimePeriodRelated: {
      get() {
        return this.$store.state.bookables.form.isTimePeriodRelated;
      },
      set(value) {
        this.updateValue({ field: "isTimePeriodRelated", value: value });
      },
    },
    allowRecurringBooking: {
      get() {
        return this.$store.state.bookables.form.allowRecurringBooking;
      },
      set(value) {
        this.updateValue({ field: "allowRecurringBooking", value: value });
      },
    },
    autoCommitBooking: {
      get() {
        return this.$store.state.bookables.form.autoCommitBooking;
      },
      set(value) {
        this.updateValue({ field: "autoCommitBooking", value: value });
      },
    },
    isBookable: {
      get() {
        if (this.$store.state.bookables.form.isBookable) {
          return this.$store.state.bookables.form.isBookable;
        } else {
          return false;
        }
      },
      set(value) {
        if (value) {
          this.updateValue({ field: "isBookable", value: value });
        } else {
          this.updateValue({ field: "isBookable", value: false });
        }
      },
    },
    isPublic: {
      get() {
        if (this.$store.state.bookables.form.isPublic) {
          return this.$store.state.bookables.form.isPublic;
        } else {
          return false;
        }
      },
      set(value) {
        if (value) {
          this.updateValue({ field: "isPublic", value: value });
        } else {
          this.updateValue({ field: "isPublic", value: false });
        }
      },
    },
    tags: {
      get() {
        return this.$store.state.bookables.form.tags;
      },
      set(value) {
        this.updateValue({ field: "tags", value: value });
      },
    },
    flags: {
      get() {
        return this.$store.state.bookables.form.flags;
      },
      set(value) {
        this.updateValue({ field: "flags", value: value });
      },
    },
    eventId: {
      get() {
        return this.$store.state.bookables.form.eventId;
      },
      set(value) {
        this.updateValue({ field: "eventId", value: value });
      },
    },
    relatedBookableIds: {
      get() {
        return this.$store.state.bookables.form.relatedBookableIds;
      },
      set(value) {
        this.updateValue({ field: "relatedBookableIds", value: value });
      },
    },
    imgUrl: {
      get() {
        return this.$store.state.bookables.form.imgUrl;
      },
      set(value) {
        this.updateValue({ field: "imgUrl", value: value });
      },
    },
    permittedUsers: {
      get() {
        return this.$store.state.bookables.form.permittedUsers;
      },
      set(value) {
        this.updateValue({ field: "permittedUsers", value: value });
      },
    },
    permittedUserGroups: {
      get() {
        return this.$store.state.bookables.form.permittedUserGroups;
      },
      set(value) {
        this.updateValue({ field: "permittedUserGroups", value: value });
      },
    },
    freeBookingUsers: {
      get() {
        return this.$store.state.bookables.form.freeBookingUsers;
      },
      set(value) {
        this.updateValue({ field: "freeBookingUsers", value: value });
      },
    },
    freeBookingUserGroups: {
      get() {
        return this.$store.state.bookables.form.freeBookingUserGroups;
      },
      set(value) {
        this.updateValue({ field: "freeBookingUserGroups", value: value });
      },
    },
    checkoutBookableIds: {
      get() {
        return this.$store.state.bookables.form.checkoutBookableIds;
      },
      set(value) {
        this.updateValue({ field: "checkoutBookableIds", value: value });
      },
    },
    commentRequired: {
      get() {
        return this.$store.state.bookables.form.requiredFields?.includes(
          "comment"
        );
      },
      set(value) {
        if (value) {
          if (
            !this.$store.state.bookables.form.requiredFields?.includes(
              "comment"
            )
          ) {
            this.updateValue({
              field: "requiredFields",
              value: [
                ...(this.$store.state.bookables.form.requiredFields || []),
                "comment",
              ],
            });
          }
        } else {
          this.updateValue({
            field: "requiredFields",
            value: (
              this.$store.state.bookables.form.requiredFields || []
            ).filter((f) => f !== "comment"),
          });
        }
      },
    },
    companyRequired: {
      get() {
        return this.$store.state.bookables.form.requiredFields?.includes(
          "company"
        );
      },
      set(value) {
        if (value) {
          if (
            !this.$store.state.bookables.form.requiredFields?.includes(
              "company"
            )
          ) {
            this.updateValue({
              field: "requiredFields",
              value: [
                ...(this.$store.state.bookables.form.requiredFields || []),
                "company",
              ],
            });
          }
        } else {
          this.updateValue({
            field: "requiredFields",
            value: (
              this.$store.state.bookables.form.requiredFields || []
            ).filter((f) => f !== "company"),
          });
        }
      },
    },
    bookingNotes: {
      get() {
        return this.$store.state.bookables.form.bookingNotes;
      },
      set(value) {
        this.updateValue({ field: "bookingNotes", value: value });
      },
    },
    mode: function () {
      return this.id ? "edit" : "create";
    },
    bookablesWithoutSelf: function () {
      if (_.isNil(this.id)) {
        return this.bookables;
      } else {
        return this.bookables.filter((b) => b.id !== this.id);
      }
    },
  },

  mounted() {
    this.initialize();
    this.allowSetPublic();
    this.setUseGraduatedPrices();
  },
};
</script>

<style scoped>
.add-time-period[disabled] {
  opacity: 0.6;
}
.panel {
  box-shadow: 0 1px 1px rgb(0 0 0 / 0.2);
}
.panel-header {
  padding: 13px 13px 13px 13px;
}
</style>
